// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- `virtual:i18n` has no file to import types from, and its module declaration reaches each app's program only through a reference
/// <reference path="./virtual.d.ts" />
import { compileMessage } from "@lingui/message-utils/compileMessage";
import { appDir, catalogs } from "virtual:i18n";
import { i18n } from "./macro-runtime";
import { COMMON_NAMESPACE, namespaceForFile } from "./namespaces";

export { i18n };

// `virtual:i18n` is generated at build time by `@web-monorepo/i18n/vite`: it slices the single,
// repo-wide `/locales/{locale}.po` into one lazy chunk per namespace, keyed
// `"{locale}/{namespace}"`. Nothing
// here reads the file system, and no namespace exists as a file. A session downloads `common` plus
// the namespaces of the routes it visits.
//
// English is a catalog like any other. Production builds of the macros strip each message's source
// text and keep only its id, so there is no inline default to fall back on: without `en` loaded, a
// message renders as its id (`vfnEbj`). For the same reason `en` is loaded underneath every other
// locale, so a string Crowdin has not translated yet shows in English rather than as an id.
const SOURCE_LOCALE = "en";

// Outside production a message may be newer than the catalog: a dev server extracts English once,
// when it starts, so a string written after that is not in it. Lingui then falls back to the message's own source text, and this compiles it
// so its placeholders and plurals still work (without it Lingui warns and shows the raw ICU).
// Production strips that source text, so there is nothing to compile, and the build drops this and
// the compiler with it.
if (import.meta.env.DEV) i18n.setMessagesCompiler(compileMessage);

/** `es-ES/common` -> `es-ES` */
const available = new Set(Object.keys(catalogs).map((key) => key.slice(0, key.indexOf("/"))));

/**
 * The catalog to use for a browser language, or null when there is none and English should be used.
 *
 * Crowdin names catalogs by region (`%locale%` expands to `es-ES`, `fr-FR`), while browsers often
 * send a bare language (`es`) or a region we have no catalog for (`es-419`). So: the exact tag,
 * then the bare language, then any catalog in the same language. The last step is what lets a
 * browser saying `es` reach `es-ES.po`; the sort makes the choice deterministic when a language has
 * more than one region.
 */
export function resolveLocale(language: string): string | null {
  const tag = language.replace("_", "-");
  const base = tag.split("-")[0] ?? tag;
  if (available.has(tag)) return tag;
  if (available.has(base)) return base;
  return [...available].sort().find((locale) => locale.split("-")[0] === base) ?? null;
}

/**
 * The browser's language, or `"en"` where there is no browser. Node has no `navigator` in older
 * runtimes and Vitest's default environment has none at all, so reading it unguarded would make
 * `activateLocale()` throw in a test rather than fall back to the source locale.
 */
function browserLanguage(): string {
  return typeof navigator === "undefined" ? "en" : navigator.language;
}

/** Null until `activateLocale` picks one. */
let activeLocale: string | null = null;

// A data router starts loading the initial route's `lazy` as soon as it is created, which can be
// before the app root has called `activateLocale`. Those loads wait on this rather than skipping
// the namespace, or the first page would render without its strings.
let markActivated: () => void = () => undefined;
const activated = new Promise<void>((resolve) => {
  markActivated = resolve;
});

/**
 * Keyed `"{locale}/{namespace}"`, so concurrent routes needing the same catalog await one fetch.
 *
 * The locale is in the key rather than implied by `activeLocale` so the cache cannot outlive the
 * locale it was filled for: a second `activateLocale` (a test, or a future language switcher)
 * starts from an empty set of hits instead of silently serving the previous locale's promises.
 */
const inFlight = new Map<string, Promise<void>>();

async function fetchNamespace(locale: string, namespace: string): Promise<void> {
  // English first, then the locale over it, both into `locale`: whatever the locale lacks keeps
  // its English text. A namespace with nothing translated yet simply has no locale chunk.
  const layers = locale === SOURCE_LOCALE ? [SOURCE_LOCALE] : [SOURCE_LOCALE, locale];
  const results = await Promise.allSettled(layers.map((layer) => catalogs[`${layer}/${namespace}`]?.()));

  // Load every layer that arrived before reporting the one that did not. If the locale's chunk
  // fails but English's arrived, the page shows English rather than ids; the rejection still
  // reaches `loadNamespace`, which evicts it so the next route load retries.
  for (const result of results) {
    if (result.status === "fulfilled" && result.value) i18n.load(locale, result.value.messages);
  }
  const failed = results.find((result) => result.status === "rejected");
  if (failed) throw failed.reason;
}

/**
 * Load one namespace by name for the active locale, waiting for `activateLocale` if it has not run
 * yet. Most callers want `loadNamespaceForFile`.
 */
export async function loadNamespace(namespace: string): Promise<void> {
  if (!activeLocale) await activated;
  const locale = activeLocale;
  if (!locale) return;

  const key = `${locale}/${namespace}`;
  let pending = inFlight.get(key);
  if (!pending) {
    // Evict on rejection, or a single transient failure (a CDN blip, a 404 against a chunk from a
    // half-rolled deploy) would be cached for the life of the session and every later navigation
    // into this namespace would re-throw it. Dropping the entry makes the next one retry.
    pending = fetchNamespace(locale, namespace).catch((error: unknown) => {
      inFlight.delete(key);
      throw error;
    });
    inFlight.set(key, pending);
  }
  return pending;
}

/**
 * Load the catalog covering `moduleKey`, an app-relative module path, and resolve once its strings
 * are live.
 *
 * Call it from the router's `lazy`, which React Router awaits before it renders the route — that
 * await is what makes the no-hook design work. There is no subscription, so a catalog arriving
 * after a component mounts could never repaint it; blocking the route on its own catalog means one
 * never does. The route chunk and the catalog chunk are fetched together, so this costs no latency.
 *
 * The catalog is repo-wide, so its `#:` origins are repo-relative (`marketplace/src/pages/...`)
 * while a router module key is app-relative (`/src/pages/...`). `appDir` comes from the same build
 * plugin that did the split, so the split and this lookup give the same answer for the same file.
 */
export function loadNamespaceForFile(moduleKey: string): Promise<void> {
  return loadNamespace(namespaceForFile(`${appDir}/${moduleKey.replace(/^\/+/, "")}`));
}

/**
 * Call once from the app's root *before* the first render.
 *
 * There is no React context and no provider: `t`, and `<Trans>` through ./macro-runtime, read the
 * `i18n` singleton at render time, so nothing needs to subscribe to it. The tradeoff is that
 * changing locale after mount would not re-render — the locale is resolved once from
 * `navigator.language` and there is no in-app language switcher, so that never happens. Awaiting
 * this before render is what removes the flash of English a provider would have.
 *
 * Only `common` is loaded here. Per-route namespaces arrive through `loadNamespaceForFile`, so the
 * startup cost is the shared strings and nothing else.
 *
 * A catalog chunk that fails to load rejects. Nothing is caught here: the app's root logs it and
 * renders through it. The failed load is not cached (see `inFlight`), so the next route load retries it.
 */
export async function activateLocale(language: string = browserLanguage()): Promise<void> {
  // A language we have no catalog for gets English, which is loaded the same way as any other.
  const locale = resolveLocale(language) ?? SOURCE_LOCALE;

  // Seed with a fresh object and activate before any catalog lands. `i18n.load` merges via
  // `Object.assign` into whatever object it holds for the locale, so seeding `{}` also keeps later
  // namespaces from being assigned into the first chunk's own exported `messages`.
  activeLocale = locale;
  i18n.load(locale, {});
  i18n.activate(locale);
  markActivated();

  await loadNamespace(COMMON_NAMESPACE);
}

/**
 * Joins items the way the active locale writes a list: `Reading, Phonics, Dyslexia` in English,
 * `Reading Phonics Dyslexia` in Japanese, `Reading, Phonics und Dyslexia` in German. Use it instead
 * of `.join(", ")`, which hard-codes English punctuation into a translated screen. The separators
 * come from the browser's CLDR data, so there is nothing to translate.
 */
export function formatList(items: readonly string[]): string {
  return new Intl.ListFormat(i18n.locale, { style: "short", type: "unit" }).format(items);
}
